<script>
import HubMapEngine from "./hubMapEngine";
import mapImageUrl from "@/assets/images/hub-map/ufa-service-hub-wide-v5.webp";
import nightMapImageUrl from "@/assets/images/hub-map/ufa-service-hub-wide-night-v5.webp";
import spinWheelImageUrl from "@/assets/images/hub-map/spin-wheel-face-day-v2.png";
import nightSpinWheelImageUrl from "@/assets/images/hub-map/spin-wheel-face-night-v2.png";

export default {
  name: "GameMapWorld",
  props: {
    locations: {
      type: Array,
      required: true
    },
    activeId: {
      type: String,
      required: true
    },
    nightMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      engine: null,
      resizeObserver: null,
      renderHook: null,
      advanceHook: null,
      zoomState: {
        percent: 100,
        canZoomIn: true,
        canZoomOut: true
      }
    };
  },
  watch: {
    activeId(nextId) {
      if (this.engine) {
        this.engine.setSelected(nextId);
      }
    },
    nightMode(nextMode) {
      if (this.engine) {
        this.engine.setNightMode(nextMode);
      }
    }
  },
  mounted() {
    const canvas = this.$refs.canvas;
    this.engine = new HubMapEngine(canvas, {
      locations: this.locations,
      mapImageUrl,
      nightMapImageUrl,
      spinWheelImageUrl,
      nightSpinWheelImageUrl,
      onSelect: id => this.$emit("select", id),
      onMovement: moving => this.$emit("movement", moving),
      onZoomChange: state => {
        this.zoomState = state;
      }
    });

    this.resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry || !this.engine) return;
      this.engine.resize(
        entry.contentRect.width,
        entry.contentRect.height,
        window.devicePixelRatio || 1
      );
    });
    this.resizeObserver.observe(this.$el);

    this.engine.setSelected(this.activeId, { immediate: true });
    this.engine.setNightMode(this.nightMode, { immediate: true });
    this.engine.start();

    this.renderHook = () => this.engine.renderGameToText();
    this.advanceHook = milliseconds => this.engine.advanceTime(milliseconds);
    window["render_game_to_text"] = this.renderHook;
    window.advanceTime = this.advanceHook;
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.engine) this.engine.stop();
    if (window["render_game_to_text"] === this.renderHook) {
      delete window["render_game_to_text"];
    }
    if (window.advanceTime === this.advanceHook) {
      delete window.advanceTime;
    }
  },
  methods: {
    handlePointerMove(event) {
      if (!this.engine) return;
      this.engine.handlePointerMove(
        event.clientX,
        event.clientY,
        event.currentTarget.getBoundingClientRect(),
        event.pointerId
      );
    },
    handlePointerDown(event) {
      if (!this.engine) return;
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      this.engine.handlePointerDown(
        event.clientX,
        event.clientY,
        event.currentTarget.getBoundingClientRect(),
        event.pointerId
      );
    },
    handlePointerUp(event) {
      if (!this.engine) return;
      this.engine.handlePointerUp(
        event.clientX,
        event.clientY,
        event.currentTarget.getBoundingClientRect(),
        event.pointerId
      );
      if (
        event.currentTarget.releasePointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    handlePointerCancel(event) {
      if (this.engine) this.engine.handlePointerCancel(event.pointerId);
      if (
        event.currentTarget.releasePointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    handlePointerLeave() {
      if (this.engine) this.engine.handlePointerLeave();
    },
    selectLocation(id) {
      if (this.engine) this.engine.setSelected(id);
      this.$emit("select", id);
    },
    zoomIn() {
      if (this.engine) this.engine.zoomByPercent(0.15);
    },
    zoomOut() {
      if (this.engine) this.engine.zoomByPercent(-0.15);
    },
    handleKeydown(event) {
      const key = event.key.toLowerCase();
      if (key === "+" || key === "=") {
        event.preventDefault();
        this.zoomIn();
        return;
      }
      if (key === "-") {
        event.preventDefault();
        this.zoomOut();
        return;
      }
      if (key === "f") {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        }
      }
    }
  },
  computed: {
    mapDescription() {
      const timeDescription = this.nightMode
        ? "ในบรรยากาศกลางคืน มีแสงจันทร์ ดาว และไฟจากอาคาร"
        : "ในบรรยากาศกลางวัน";
      return `แผนที่เกาะบริการ UFA Easy888 ${timeDescription} มีประตูจอยเกมเข้าเล่นเป็นศูนย์กลาง และมีอาคารบริการหกแห่งล้อมรอบ ได้แก่ ธนาคารฝากเหรียญ ตู้เซฟถอนเหรียญ วงล้อ Spin คาสิโน Roulette ร้านของรางวัล และกิลด์แนะนำเพื่อน ลากเพื่อสำรวจ จีบนิ้วเพื่อซูม และแตะอาคารเพื่อเลือก`;
    }
  }
};
</script>

<template>
  <div class="map-world">
    <canvas
      ref="canvas"
      class="map-world__canvas"
      role="img"
      :aria-label="mapDescription"
      @pointermove.prevent="handlePointerMove"
      @pointerdown.prevent="handlePointerDown"
      @pointerup.prevent="handlePointerUp"
      @pointercancel="handlePointerCancel"
      @pointerleave="handlePointerLeave"
    ></canvas>

    <div class="map-world__zoom-controls" aria-label="ควบคุมการซูมแผนที่">
      <button
        type="button"
        aria-label="ซูมเข้าแผนที่"
        :disabled="!zoomState.canZoomIn"
        @click="zoomIn"
      >
        <i class="fa fa-plus" aria-hidden="true"></i>
      </button>
      <output aria-live="polite">{{ zoomState.percent }}%</output>
      <button
        type="button"
        aria-label="ซูมออกแผนที่"
        :disabled="!zoomState.canZoomOut"
        @click="zoomOut"
      >
        <i class="fa fa-minus" aria-hidden="true"></i>
      </button>
    </div>

    <nav class="map-world__accessible-nav" aria-label="เลือกพื้นที่บนแผนที่">
      <button
        v-for="location in locations"
        :key="location.id"
        type="button"
        @click="selectLocation(location.id)"
      >
        {{ location.label }}
      </button>
    </nav>
  </div>
</template>

<style scoped>
.map-world {
  position: absolute;
  inset: 0;
  overflow: hidden;
  touch-action: none;
}

.map-world__canvas {
  display: block;
  width: 100%;
  height: 100%;
  outline: 0;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
}

.map-world__zoom-controls {
  position: absolute;
  top: 170px;
  right: 14px;
  z-index: 5;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 17px;
  background: rgba(8, 34, 51, 0.72);
  box-shadow: 0 9px 24px rgba(3, 18, 31, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.map-world__zoom-controls button {
  appearance: none;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  padding: 0;
  color: #ffffff;
  border: 0;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 13px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.map-world__zoom-controls button:active {
  background: rgba(255, 226, 154, 0.22);
  transform: scale(0.94);
}

.map-world__zoom-controls button:disabled {
  color: rgba(255, 255, 255, 0.32);
  cursor: default;
  opacity: 0.55;
}

.map-world__zoom-controls button:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}

.map-world__zoom-controls output {
  display: grid;
  place-items: center;
  width: 42px;
  min-height: 24px;
  color: #ffe39a;
  font-family: "Poppins", sans-serif;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.map-world__accessible-nav {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  white-space: nowrap;
}
</style>
